import React from 'react';
import { Helmet } from 'react-helmet-async';

function stripHtml(text) {
  return (text || '').toString().replace(/<[^>]*>/g, ' ');
}

function compactWhitespace(text) {
  return stripHtml(text).replace(/\s+/g, ' ').trim();
}

function defaultCanonical() {
  if (typeof window === 'undefined') return '';
  try {
    const url = new URL(window.location.href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
}

function getOriginFromUrl(url) {
  try {
    if (!url) return '';
    const u = new URL(url);
    return u.origin;
  } catch {
    return '';
  }
}

function toAbsoluteUrl(maybeRelativeUrl, origin) {
  const value = (maybeRelativeUrl || '').toString().trim();
  if (!value) return '';

  // Already absolute
  if (/^https?:\/\//i.test(value)) return value;

  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!base) return value;
  try {
    return new URL(value, base).toString();
  } catch {
    return value;
  }
}

function normalizeStructuredData(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input.filter(Boolean);
  return [input];
}

export default function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  noIndex = false,
  canonical,
  structuredData,
  locale = 'en_EG',
  htmlLang,
  htmlDir,
  includeSiteStructuredData = true
}) {
  const resolvedTitle = title ? `${title}` : 'VetCare Store';
  const resolvedDescription = description
    ? compactWhitespace(description).slice(0, 200)
    : 'VetCare Store - Quality pet supplies, food, and essentials in Egypt. Fast delivery across Egypt.';

  const resolvedCanonical = canonical || defaultCanonical();
  const resolvedUrl = url || resolvedCanonical;

  const origin = getOriginFromUrl(resolvedCanonical) || (typeof window !== 'undefined' ? window.location.origin : '');
  const resolvedImage = toAbsoluteUrl(image || '/assets/images/logo.png', origin);

  const siteStructured = includeSiteStructuredData && origin ? [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'VetCare Store',
      url: origin,
      logo: toAbsoluteUrl('/assets/images/logo.png', origin)
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'VetCare Store',
      url: origin,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${origin}/shop?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    }
  ] : [];

  const structuredBlocks = [...siteStructured, ...normalizeStructuredData(structuredData)];

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={locale === 'ar_EG' ? 'en_EG' : 'ar_EG'} />

      {/* Geo targeting (non-critical hints) */}
      <meta name="geo.region" content="EG" />
      <meta name="geo.placename" content="Egypt" />

      {(htmlLang || htmlDir) ? (
        <html lang={htmlLang || undefined} dir={htmlDir || undefined} />
      ) : null}

      {resolvedCanonical ? <link rel="canonical" href={resolvedCanonical} /> : null}

      <meta property="og:site_name" content="VetCare Store" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      {resolvedUrl ? <meta property="og:url" content={resolvedUrl} /> : null}
      <meta property="og:image" content={resolvedImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {structuredBlocks.map((block, idx) => (
        <script key={idx} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}
